import {
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import { SOFTLINE_CONFIG_SEND_NOTIFICATION_COMPONENT } from '@softapps/core/notification';
import {
  provideSearchModule,
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import {
  ApplicationStore,
  getModuleGroupById,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_DETAIL_SERVICE_FACTORY,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SOFTLINE_STORE_REMOTE_OBJECT_SERVICE,
  SoftlineEntityDetailService,
  SoftlineEntityService,
  SoftlineObjectService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import { from, map, Observable, of } from 'rxjs';
import { default as de } from './../i18n/de.json';
import { default as deTitles } from './../i18n/de.titles.json';
import { MitarbeiterSearchResult } from './components/item-search-result/employee-search-result.component';
import { StatusabfrageEmpfaengerAuswahlComponent } from './components/statusabfrage-empfaenger-auswahl/statusabfrage-empfaenger-auswahl.component';
import { AbteilungenService } from './services/abteilungen.service';
import {
  SOFTLINE_API_PMS_STATUSABFRAGE_ABTEILUNGEN,
  SOFTLINE_API_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
  SOFTLINE_API_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
} from './statusabfrage.api';
import {
  SOFTLINE_CONFIG_PMS_STATUSABFRAGE_SEND_NOTIFICATION_COMPONENT,
  SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
  SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
  SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
  SOFTLINE_PERMISSION_PMS_STATUSABFRAGE,
} from './statusabfrage.shared';
import { statusabfrageRoutes } from './statusabfrage.routes';
import { provideStatusabfrageGlobals } from './statusabfrage.globals';
import { PmsAbteilungStore } from './store/abteilung.store';
import { PmsPersonalDetailStore } from './store/personal-detail.store';
import { PmsPersonalStore } from './store/personal.store';
import { PersonalUebersicht } from './types/abteilung';

export function provideStatusabfrageModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'pmsStatusabfrage',
        title: 'Statusabfrage',
        icon: 'fa-regular fa-user-clock',
        route: '/statusabfrage',
        color: 'bg-[#0177BF] text-white',
        permission: SOFTLINE_PERMISSION_PMS_STATUSABFRAGE,
        group: getModuleGroupById(8),
      },
      withSoftAppsModuleRoutes({ app: statusabfrageRoutes }),
      withLazySoftAppsModuleTranslation({
        eager: { de: deTitles },
        lazy: { de: () => import('./../i18n/de.json') },
      }),
      withSoftAppsModuleInfo({
        library: '@softapps/pms/statusabfrage',
        description: 'Anwesenheitsstatus der Mitarbeiter abfragen.',
      })
    ),
    ...provideStatusabfrageGlobals(),
  ];
}

export const statusabfrageProviders = [
  provideEnvironmentInitializer(async () => {
    const store = inject(Store);

    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'pmsStatusabfrage',
      language: 'de',
      translations: de,
    });

    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: 'Statusabfrage',
      route: '/statusabfrage',
      color: 'bg-[#0177BF] text-white',
      icon: 'fa-regular fa-user-clock',
      permission: SOFTLINE_PERMISSION_PMS_STATUSABFRAGE,
      group: getModuleGroupById(8), // Personalmanagement
    });
  }),
  importProvidersFrom(
    StoreModule.forFeature({
      feature: PmsPersonalStore.feature,
      name: SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () =>
            new SoftlineEntityService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER
            ),
        },
        {
          provide: SOFTLINE_STORE_DETAIL_SERVICE_FACTORY,
          useFactory:
            (service = inject(SOFTLINE_SERVICE_HTTP)) =>
            () =>
              new SoftlineEntityDetailService(
                service,
                SOFTLINE_API_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
                'detail/{{idlstfix}}'
              ),
        },
      ],
    }),
    StoreModule.forFeature({
      feature: PmsPersonalDetailStore.feature,
      name: SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
      providers: [
        {
          provide: SOFTLINE_STORE_REMOTE_OBJECT_SERVICE,
          useFactory: () =>
            new SoftlineObjectService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL
            ),
        },
      ],
    }),
    StoreModule.forFeature({
      feature: PmsAbteilungStore.feature,
      name: SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () =>
            new SoftlineEntityService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_PMS_STATUSABFRAGE_ABTEILUNGEN
            ),
        },
      ],
    })
  ),
  AbteilungenService,
  {
    provide: SOFTLINE_CONFIG_SEND_NOTIFICATION_COMPONENT,
    multi: true,
    useValue: {
      name: SOFTLINE_CONFIG_PMS_STATUSABFRAGE_SEND_NOTIFICATION_COMPONENT,
      component: StatusabfrageEmpfaengerAuswahlComponent,
    },
  },
  provideSearchModule<PersonalUebersicht>({
    url: '/statusabfrage',
    category: 'Statusabfrage',
    icon: 'fa-regular fa-user-clock',
    permission: SOFTLINE_PERMISSION_PMS_STATUSABFRAGE,
    component: MitarbeiterSearchResult,
    factory: () => {
      const store = inject(Store);

      return {
        results(term: string | null): Observable<PersonalUebersicht[]> {
          if (!term || term.trim().length === 0) {
            return of([]);
          }
          const today = new Date().toISOString().slice(0, 10);
          return from(
            store.dispatch(
              SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
              PmsPersonalStore.actions.loadMany,
              { pathParams: { stichdatum: today } }
            )
          ).pipe(
            map((mitarbeiter: PersonalUebersicht[]) => {
              const normalizedTerm = term.toLowerCase().trim();
              const seen = new Set<number>();
              return mitarbeiter.filter((m) => {
                if (seen.has(m.id)) return false;
                seen.add(m.id);
                return (
                  m.anwender?.firstName?.toLowerCase().includes(normalizedTerm) ||
                  m.anwender?.lastName?.toLowerCase().includes(normalizedTerm) ||
                  m.anwender?.user?.toLowerCase().includes(normalizedTerm) ||
                  m.lstfix?.vorname?.toLowerCase().includes(normalizedTerm) ||
                  m.lstfix?.zuname?.toLowerCase().includes(normalizedTerm) ||
                  m.abteilung?.bezeichnung?.toLowerCase().includes(normalizedTerm)
                );
              });
            })
          );
        },
      };
    },
  }),
];
